
import React from 'react';
import { Link } from 'react-router-dom';
import OptimizedImage from '../OptimizedImage';
import { CartItem } from '../../types';
import CartItemRow from './CartItemRow';
import SwipeableCartItem from './SwipeableCartItem';
import { motion, AnimatePresence } from 'framer-motion';

interface CartSellerGroupProps {
    sellerId: string;
    items: CartItem[];
    onRemove: (listingId: string) => void;
    onCheckout?: (sellerId: string, sellerName: string, amount: number) => void;
    isMobile?: boolean;
}

const CartSellerGroup: React.FC<CartSellerGroupProps> = ({
    sellerId,
    items,
    onRemove,
    onCheckout,
    isMobile = false
}) => {
    // Derived values
    const sellerName = items[0]?.sellerName || 'Satıcı';
    const sellerTotal = items.reduce((sum, i) => sum + (i.priceAtAdd || 0), 0);
    const itemCount = items.length;

    return (
        <div className="bg-white dark:bg-gray-950 rounded-[2rem] p-6 shadow-sm border border-gray-100 dark:border-gray-800 space-y-6">
            {/* Seller Header */}
            <div className="flex items-center justify-between border-b border-gray-50 dark:border-gray-900 pb-5">
                <Link to={`/profile/${sellerId}`} className="group flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-xl border border-gray-100 dark:border-gray-800 group-hover:scale-110 transition-transform">🏪</div>
                    <div className="flex flex-col">
                        <span className="font-black text-gray-950 dark:text-white uppercase tracking-tight group-hover:text-blue-500 transition-colors">{sellerName}</span>
                        <span className="text-[10px] font-black text-amber-500 uppercase tracking-widest">Doğrulanmış Satıcı</span>
                    </div>
                </Link>
                <span className="text-[9px] font-black uppercase tracking-widest bg-gray-50 dark:bg-gray-900 px-3 py-1.5 rounded-lg text-gray-400 dark:text-gray-500 border border-gray-100 dark:border-gray-800">{itemCount} Ürün</span>
            </div>

            {/* Items List */}
            <div className="space-y-4">
                <AnimatePresence mode='popLayout'>
                    {items.map((item) => {
                        const ItemComponent = (
                            <CartItemRow
                                key={item.listingId}
                                item={item}
                                onRemove={onRemove}
                            />
                        );

                        return (
                            <motion.div
                                key={item.listingId}
                                layout
                                initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                {isMobile ? (
                                    <SwipeableCartItem key={item.listingId} onRemove={() => onRemove(item.listingId)}>
                                        {ItemComponent}
                                    </SwipeableCartItem>
                                ) : (
                                    ItemComponent
                                )}
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            {/* Footer / Subtotal for this Seller */}
            <div className="pt-5 border-t border-gray-50 dark:border-gray-900 flex justify-between items-center">
                <span className="text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest">Satıcı Toplamı</span>
                <span className="text-2xl font-black text-gray-950 dark:text-white uppercase tracking-tighter italic">₺{sellerTotal.toLocaleString('tr-TR')}</span>
            </div>

            {/* Per-Seller Checkout Button (Optional / Context Dependent) */}
            {onCheckout && (
                <button
                    onClick={() => onCheckout(sellerId, sellerName, sellerTotal)}
                    className="w-full py-4 bg-gray-50 dark:bg-gray-900 text-gray-950 dark:text-white hover:bg-gray-950 dark:hover:bg-white hover:text-white dark:hover:text-gray-950 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all border border-gray-100 dark:border-gray-800 active:scale-[0.98]"
                >
                    Sadece Bu Satıcıdan Al
                </button>
            )}
        </div>
    );
};

export default CartSellerGroup;
